import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, RefreshCw, KeyRound } from "lucide-react";
import { api } from "@/lib/api";

const EP_LABELS = { catalog: "Catalog (products)", orders: "Orders", customers: "Customers", reports: "Reports" };

export function ApiKeyCard() {
  const [keyInfo, setKeyInfo] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [namedKeys, setNamedKeys] = useState([]);
  const [newName, setNewName] = useState("");
  const [newScope, setNewScope] = useState("read");
  const [newEndpoints, setNewEndpoints] = useState(["catalog"]);
  const [endpointGroups, setEndpointGroups] = useState({});
  const [editKeyId, setEditKeyId] = useState(null);
  const [createdKey, setCreatedKey] = useState(null);
  const [creating, setCreating] = useState(false);

  const copyCode = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard!"); };

  const loadNamedKeys = async () => {
    try { const { data } = await api.get("/admin/api-keys"); setNamedKeys(data); }
    catch (e) { console.warn("api-keys load failed", e); }
    try { const { data } = await api.get("/admin/api-keys/endpoint-groups"); setEndpointGroups(data); }
    catch (e) { console.warn("endpoint-groups load failed", e); }
  };

  const toggleKeyEndpoint = async (k, g) => {
    const cur = k.endpoints || ["catalog"];
    const next = cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g];
    if (!next.length) { toast.error("Kam se kam ek endpoint group chunein"); return; }
    try { await api.put(`/admin/api-keys/${k.id}/endpoints`, { endpoints: next }); loadNamedKeys(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const createNamedKey = async () => {
    if (!newName.trim()) { toast.error("Key ka naam daalein (jaise 'Mobile App')"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/api-keys", { name: newName.trim(), scope: newScope, ...(newScope === "read" ? { endpoints: newEndpoints } : {}) });
      setCreatedKey(data);
      setNewName("");
      toast.success("Nayi key ban gayi — abhi copy kar lein");
      loadNamedKeys();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  const revokeNamedKey = async (id, name) => {
    if (!window.confirm(`"${name}" key band kar dein? Jo bhi is key se connected hai wo turant kaam karna band kar dega.`)) return;
    try { await api.delete(`/admin/api-keys/${id}`); toast.success("Key revoked"); loadNamedKeys(); }
    catch (e) { toast.error("Revoke failed"); }
  };

  const loadKeyInfo = async () => {
    try { const { data } = await api.get("/admin/api-key"); setKeyInfo(data); }
    catch (e) { console.warn("api-key info load failed", e); }
  };

  const rotateKey = async () => {
    if (!window.confirm("Nayi API key banegi — purani key turant band ho jayegi. Jis bhi app/tool me key lagi hai use update karna hoga. Continue?")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/admin/api-key/rotate");
      setNewKey(data.api_key);
      toast.success("Nayi API key ban gayi — abhi copy kar lein, baad me sirf masked dikhegi");
      loadKeyInfo();
    } catch (e) { toast.error(e.response?.data?.detail || "Rotate failed"); }
    finally { setRotating(false); }
  };

  useEffect(() => { loadKeyInfo(); loadNamedKeys(); }, []);

  return (
    <div data-testid="api-key-card" className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <KeyRound className="text-indigo-600" size={20} />
          <div>
            <h2 className="font-bold text-base text-slate-900">API Key</h2>
            <p className="text-xs text-slate-500">Server-to-server / mobile integrations ke liye. Header <code className="bg-slate-100 px-1 rounded">X-API-Key</code> me use karein.</p>
          </div>
        </div>
        <button data-testid="api-key-rotate-btn" onClick={rotateKey} disabled={rotating}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-lg font-bold text-xs flex items-center gap-1.5 self-start">
          <RefreshCw size={14} className={rotating ? "animate-spin" : ""} /> {rotating ? "Rotating…" : "Rotate API Key"}
        </button>
      </div>
      {newKey ? (
        <div data-testid="api-key-new" className="mt-3 rounded-xl border-2 border-amber-300 bg-amber-50 p-3">
          <p className="text-xs font-bold text-amber-700 mb-1">⚠️ Nayi key — abhi copy kar lein, ye sirf ek baar dikhti hai:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white border border-amber-200 rounded-lg px-3 py-2 break-all" data-testid="api-key-new-value">{newKey}</code>
            <button data-testid="api-key-copy-btn" onClick={() => copyCode(newKey)} className="px-3 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shrink-0"><Copy size={13} /> Copy</button>
          </div>
        </div>
      ) : keyInfo?.masked && (
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <code className="text-xs bg-slate-100 border border-slate-200 rounded-lg px-3 py-2" data-testid="api-key-masked">{keyInfo.masked}</code>
          {keyInfo.rotated_at && <span className="text-[11px] text-slate-500">Last rotated: {new Date(keyInfo.rotated_at).toLocaleString("en-IN")}{keyInfo.rotated_by ? ` · by ${keyInfo.rotated_by}` : ""}</span>}
        </div>
      )}

      {/* Per-integration named keys */}
      <div className="mt-5 pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-bold text-slate-800">Per-Integration Keys</p>
          <Link to="/admin/api-analytics" data-testid="view-api-analytics-link" className="text-xs font-bold text-indigo-600 hover:text-indigo-700">Usage Analytics →</Link>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">Har app/tool ke liye alag key banayein. <b>Read-only</b> key sirf data padh sakti hai (kuch badal nahi sakti) — leak ho to bhi safe.</p>

        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <input data-testid="new-key-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Key ka naam (jaise Mobile App, Analytics Tool)" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <select data-testid="new-key-scope" value={newScope} onChange={(e) => setNewScope(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="read">Read-only (safe)</option>
            <option value="full">Full access</option>
          </select>
          <button data-testid="create-key-btn" onClick={createNamedKey} disabled={creating} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-lg font-bold text-xs whitespace-nowrap">{creating ? "Creating…" : "+ Create Key"}</button>
        </div>

        {newScope === "read" && (
          <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="new-key-endpoints">
            <span className="text-[11px] font-bold text-slate-500 uppercase">Allowed endpoints:</span>
            {Object.keys(endpointGroups).map((g) => (
              <label key={g} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                <input type="checkbox" data-testid={`new-key-ep-${g}`} checked={newEndpoints.includes(g)}
                  onChange={(e) => setNewEndpoints(e.target.checked ? [...newEndpoints, g] : newEndpoints.filter((x) => x !== g))}
                  className="accent-indigo-600 w-4 h-4" />
                {EP_LABELS[g] || g}
              </label>
            ))}
            <p className="w-full text-[10px] text-slate-400">Read-only key sirf chune hue endpoint groups access kar payegi — baaki sab par 403</p>
          </div>
        )}

        {createdKey && (
          <div data-testid="created-key-box" className="mb-3 rounded-xl border-2 border-amber-300 bg-amber-50 p-3">
            <p className="text-xs font-bold text-amber-700 mb-1">⚠️ "{createdKey.name}" ({createdKey.scope}) — abhi copy kar lein, ye sirf ek baar dikhegi:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white border border-amber-200 rounded-lg px-3 py-2 break-all" data-testid="created-key-value">{createdKey.api_key}</code>
              <button onClick={() => copyCode(createdKey.api_key)} className="px-3 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shrink-0"><Copy size={13} /> Copy</button>
            </div>
          </div>
        )}

        {namedKeys.length > 0 && (
          <div className="space-y-2" data-testid="named-keys-list">
            {namedKeys.map((k) => (
              <div key={k.id}>
              <div data-testid={`named-key-${k.id}`} className={`flex items-center gap-3 flex-wrap rounded-lg border px-3 py-2 ${k.active ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-60"}`}>
                <span className="font-semibold text-sm text-slate-800">{k.name}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${k.scope === "read" ? "bg-sky-100 text-sky-700" : "bg-purple-100 text-purple-700"}`}>{k.scope === "read" ? "READ-ONLY" : "FULL"}</span>
                <code className="text-xs text-slate-500">{k.masked}</code>
                {k.scope === "read" && (k.endpoints || ["catalog"]).map((g) => <span key={g} className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 uppercase">{g}</span>)}
                {!k.active && <span className="text-[10px] font-bold text-rose-500">REVOKED</span>}
                <span className="text-[11px] text-slate-400 ml-auto">{k.last_used ? `used ${new Date(k.last_used).toLocaleDateString("en-IN")}` : "never used"}</span>
                {k.active && k.scope === "read" && <button data-testid={`edit-eps-${k.id}`} onClick={() => setEditKeyId(editKeyId === k.id ? null : k.id)} className="text-xs font-bold text-indigo-600 hover:text-indigo-700">Endpoints</button>}
                {k.active && <button data-testid={`revoke-key-${k.id}`} onClick={() => revokeNamedKey(k.id, k.name)} className="text-xs font-bold text-rose-600 hover:text-rose-700">Revoke</button>}
              </div>
              {editKeyId === k.id && (
                <div className="mt-1 rounded-lg border border-indigo-100 bg-indigo-50/50 px-3 py-2 flex flex-wrap items-center gap-3" data-testid={`eps-editor-${k.id}`}>
                  <span className="text-[10px] font-bold text-slate-500 uppercase">Allowed:</span>
                  {Object.keys(endpointGroups).map((g) => (
                    <label key={g} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer">
                      <input type="checkbox" data-testid={`eps-${k.id}-${g}`} checked={(k.endpoints || ["catalog"]).includes(g)} onChange={() => toggleKeyEndpoint(k, g)} className="accent-indigo-600 w-4 h-4" />
                      {EP_LABELS[g] || g}
                    </label>
                  ))}
                  <span className="text-[10px] text-slate-400">change turant lagoo hota hai</span>
                </div>
              )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
